import { observer } from "mobx-react";
import { useMemo, useState } from "react";

import { reportFilterStatus } from "client";
import Button from "components/UI/Button/Button";
import { ButtonWithIcon } from "components/UI/ButtonWithIcon/ButtonWithIcon";

import rightArrow from "assets/images/accordionred.svg";

import "./ProjectsFilterReports.scss";

const REPORT_FILTERS = {
  ALL: {
    label: "Все",
    statuses: null,
  },
  READY: {
    label: "Ожидают проверки",
    statuses: ["ready"],
  },
  SEARCH: {
    label: "Поиск исполнителя",
    statuses: ["search"],
  },
  IN_PROGRESS: {
    label: "В работе",
    statuses: ["run"],
  },
  DRAFT: {
    label: "В планах",
    statuses: ["new"],
  },
};

const STATUS_TO_LABEL_MAP = {
  "ready": "Ожидают проверки",
  "search": "Поиск исполнителя",
  "run": "В работе",
  "new": "В планах",
};

const ProjectsFilterReports = observer(({ store, failedProjects, onFilterChange, isLoading }) => {
  const [selectedFilter, setSelectedFilter] = useState(REPORT_FILTERS.ALL.label);

  const statusCounts = useMemo(() => {
    const counts = {
      "Все": 0,
      "Ожидают проверки": 0,
      "Поиск исполнителя": 0,
      "В работе": 0,
      "В планах": 0,
    };

    const projectsToCount = store.filteredProjects || store.projects;

    if (!projectsToCount || !Array.isArray(projectsToCount)) {
      return counts;
    }

    const projectsWithNewReports = [];
    
    projectsToCount.forEach(project => {
      const shouldShowProject = project.status !== "finished";
      
      if (!shouldShowProject) {
        return;
      }
      
      if (project.reports && Array.isArray(project.reports)) {
        const newReportsCount = project.reports.filter(r => r.status === "new").length;
        if (newReportsCount > 0) {
          projectsWithNewReports.push({
            id: project.id,
            newReports: newReportsCount,
            totalReports: project.reports.length,
            status: project.status,
            title: project.title
          });
        }
        
        project.reports.forEach(report => {
          counts["Все"]++;
          
          const labelKey = STATUS_TO_LABEL_MAP[reportFilterStatus(report)];
          if (labelKey) {
            counts[labelKey]++;
          }
        });
      }
    });

    return counts;
  }, [store.projects, store.filteredProjects]);

  const handleFilterSelect = (filter) => {
    store.selectedCities.active = {};
    store.resetFailedFilter();
    setSelectedFilter(filter.label);
    store.setReportStatusFilter(filter.statuses);
    onFilterChange();
  };

  const getCounterClassName = (filter) => {
    if (filter.label === REPORT_FILTERS.READY.label && statusCounts[filter.label] > 0) {
      return "projects-filter-reports__counter projects-filter-reports__counter-green";
    }
    return "projects-filter-reports__counter";
  };

  const renderCounterContent = (filter) => {
    if (!store.projects) return <div className="projects-filter-reports__counter-skeleton" />;
    
    const count = statusCounts[filter.label];
    return (
      <span className="projects-filter-reports__counter-value">
        {count > 99 ? "99+" : count || 0}
      </span>
    );
  };

  return (
    <div className={`projects-filter-reports ${failedProjects?.length ? "space-between" : ""}`}>
      <div className="projects-filter-reports__container">
        {Object.values(REPORT_FILTERS).map((filter) => (
          <div className="projects-filter-reports__button-container" key={filter.label}>
            <Button
              title={filter.label}
              className={`projects-filter-reports__button ${selectedFilter === filter.label ? "active" : ""}`}
              onClick={() => handleFilterSelect(filter)}
              disabled={filter.label !== REPORT_FILTERS.ALL.label && statusCounts[filter.label] === 0}
            />
            {filter.label !== REPORT_FILTERS.ALL.label && (
              <div className={getCounterClassName(filter)}>
                {renderCounterContent(filter)}
              </div>
            )}
          </div>
        ))}
      </div>

      {failedProjects?.length > 0 && (
        <div className="projects-filter-reports__errors">
          <div className="projects-filter-reports__errors-container">
            <div className="projects-filter-reports__errors-icon" />
            <p className="projects-filter-reports__errors-text">
              {failedProjects.length}{" "}
              {failedProjects.length > 1 ? "ошибки" : "ошибка"}
            </p>
          </div>
          <ButtonWithIcon
            title="Показать в списке"
            className="projects-filter-reports__errors-button"
            icon={rightArrow}
            onClick={() => {
              store.activateFailedFilter();
              setSelectedFilter(REPORT_FILTERS.ALL.label);
              onFilterChange();
            }}
            ariaLabel="Перейти к проектам со статусом Ошибка"
          />
        </div>
      )}
    </div>
  );
});

export default ProjectsFilterReports;